import React from "react";
import { createRoot } from "react-dom/client";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import ThemeModeProvider from "@theme/ThemeModeProvider";
import MenuShell from "./MenuShell";
import {
  applyMenuDevOverrides,
  looksLikeJson,
  mergeMenuExtensions,
  normalizeExtensions,
  normalizeMenuPayload,
  parseDevOverrides,
} from "./menu.utils";

const LOADING_REMOVAL_DELAY_MS = 16;
const MENU_POLLING_INTERVAL_MS = 120000;
const DRAWER_COLLAPSED_WIDTH = 60;
const DRAWER_OPEN_WIDTH = 280;
const MOBILE_TOP_BAR_HEIGHT = 0;
const MOBILE_BOTTOM_BAR_HEIGHT = 58;
const MOBILE_VERTICAL_OFFSETS = MOBILE_TOP_BAR_HEIGHT + MOBILE_BOTTOM_BAR_HEIGHT;
const MENU_HOST_Z_INDEX = 110;

export default function initializeAppMenu() {
  if (customElements.get("app-menu")) {
    return;
  }

  class AppMenu extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.reactRoot = null;
      this.mountNode = null;
      this.pollingTimer = null;
      this.refreshingMenu = false;
      this.connected = false;
      this.layoutReady = false;
      this.contentSelector = "#app";
      this.contentElement = null;
      this.contentStylesSnapshot = null;
      this.hostStylesSnapshot = null;
      this.currentPayload = null;
      this.menuSrc = "";
      this.menuLogoutUrl = "/logout";
      this.menuHostBase = typeof window !== "undefined" ? window.location.origin : "";
      this.menuExtensions = [];
      this.menuExtendPosition = "bottom";
      this.menuDevOverrides = {};
      this.emotionCache = null;
      this.onLayoutStateChange = this.onLayoutStateChange.bind(this);
      this.refreshMenuPayload = this.refreshMenuPayload.bind(this);
      this.setAttribute("data-menu-mobile", "0");
      this.setAttribute("data-menu-open", "0");
      this.setAttribute("data-menu-desktop-open", "0");
      this.setAttribute("data-menu-mobile-open", "0");
    }

    async connectedCallback() {
      if (this.connected) {
        return;
      }
      this.connected = true;
      this.contentSelector = this.getAttribute("app-content") || "#app";

      const src = this.getAttribute("src") || "";
      const logoutUrl = this.getAttribute("logout") || "/logout";
      const extendSrc = this.getAttribute("extend-src") || this.getAttribute("extend") || "";
      const extendPosition = (this.getAttribute("extend-position") || "bottom").toLowerCase();
      const devOverrides = parseDevOverrides(this.getAttribute("dev-overrides") || "");
      const hostBase = this.getAttribute("data-host") || window.location.origin;
      this.menuSrc = src;
      this.menuLogoutUrl = logoutUrl;
      this.menuHostBase = hostBase;
      this.menuExtendPosition = extendPosition;
      this.menuDevOverrides = devOverrides;

      if (!src) {
        this.renderError("No se encontro el atributo src para cargar el menu.");
        return;
      }

      try {
        const [payloadRaw, extensions] = await Promise.all([
          fetchMenuPayload(src, logoutUrl),
          getExtensions(extendSrc),
        ]);
        this.menuExtensions = Array.isArray(extensions) ? extensions : [];
        const payload = this.buildMenuPayload(payloadRaw);
        this.mountReact(payload, hostBase);
        this.startMenuPolling();
      } catch (error) {
        console.error("app-menu: react mount failed", error);
        this.renderError("No fue posible cargar el menu React.");
      }
    }

    disconnectedCallback() {
      this.connected = false;
      this.layoutReady = false;
      this.stopMenuPolling();
      this.refreshingMenu = false;
      if (this.reactRoot) {
        this.reactRoot.unmount();
      }
      this.reactRoot = null;
      this.mountNode = null;
      this.currentPayload = null;
      this.emotionCache = null;
      if (this.shadowRoot) {
        this.shadowRoot.replaceChildren();
      }
      this.setAttribute("data-menu-mobile", "0");
      this.setAttribute("data-menu-open", "0");
      this.setAttribute("data-menu-desktop-open", "0");
      this.setAttribute("data-menu-mobile-open", "0");
      this.restoreContentStyles();
      this.restoreHostStyles();
    }

    mountReact(payload, hostBase) {
      if (!this.shadowRoot) {
        return;
      }

      this.shadowRoot.replaceChildren();

      // Keep outsider menu visual source of truth in MUI + src/theme only.
      // Do not inject legacy css links returned by payload to avoid style conflicts.

      this.mountNode = document.createElement("div");
      this.mountNode.setAttribute("part", "menu-root");
      this.shadowRoot.appendChild(this.mountNode);

      this.currentPayload = payload;
      this.menuHostBase = hostBase;
      this.emotionCache = createCache({
        key: "app-menu",
        container: this.shadowRoot,
      });
      this.reactRoot = createRoot(this.mountNode);
      this.renderReactTree();

      this.layoutReady = true;
      const initialMobile = typeof window !== "undefined" ? window.matchMedia?.("(max-width:1100px)")?.matches : false;
      this.applyHostLayoutStyles();
      this.applyHostLayoutByState({ isMobile: Boolean(initialMobile) });
      this.applyContentLayout({
        isMobile: Boolean(initialMobile),
        desktopOpen: false,
        mobileOpen: false,
      });

      window.setTimeout(() => {
        this.removeAttribute("loading");
      }, LOADING_REMOVAL_DELAY_MS);
    }

    renderReactTree() {
      if (!this.reactRoot) {
        return;
      }
      if (!this.emotionCache) {
        this.emotionCache = createCache({
          key: "app-menu",
          container: this.shadowRoot,
        });
      }

      this.reactRoot.render(
        <CacheProvider value={this.emotionCache}>
          <ThemeModeProvider>
            <MenuShell
              payload={this.currentPayload}
              hostBase={this.menuHostBase}
              onLayoutStateChange={this.onLayoutStateChange}
              onNotificationsClick={this.refreshMenuPayload}
            />
          </ThemeModeProvider>
        </CacheProvider>
      );
    }

    buildMenuPayload(payloadRaw) {
      const payload = normalizeMenuPayload(payloadRaw, {
        logoutUrl: this.menuLogoutUrl,
        perfilUrl: `${this.menuHostBase.replace(/\/$/, "")}/-/perfil`,
        hostBase: this.menuHostBase,
      });
      payload.menu = mergeMenuExtensions(payload.menu, this.menuExtensions, this.menuExtendPosition);
      payload.menu = applyMenuDevOverrides(payload.menu, this.menuDevOverrides);
      return payload;
    }

    startMenuPolling() {
      this.stopMenuPolling();
      this.pollingTimer = window.setInterval(() => {
        this.refreshMenuPayload({ silent: true, forceRefresh: true });
      }, MENU_POLLING_INTERVAL_MS);
    }

    stopMenuPolling() {
      if (!this.pollingTimer) {
        return;
      }
      window.clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    async refreshMenuPayload(options = {}) {
      const { silent = false, forceRefresh = true } = options;
      if (!this.connected || !this.menuSrc || this.refreshingMenu) {
        return;
      }

      this.refreshingMenu = true;
      try {
        const payloadRaw = await fetchMenuPayload(this.menuSrc, this.menuLogoutUrl, { forceRefresh });
        this.currentPayload = this.buildMenuPayload(payloadRaw);
        this.renderReactTree();
      } catch (error) {
        if (!silent) {
          console.warn("app-menu: refresh menu payload failed", error);
        }
      } finally {
        this.refreshingMenu = false;
      }
    }

    onLayoutStateChange(state = {}) {
      const isMobile = Boolean(state.isMobile);
      const desktopOpen = Boolean(state.desktopOpen);
      const mobileOpen = Boolean(state.mobileOpen);
      const isOpen = isMobile ? mobileOpen : desktopOpen;

      this.setAttribute("data-menu-mobile", isMobile ? "1" : "0");
      this.setAttribute("data-menu-open", isOpen ? "1" : "0");
      this.setAttribute("data-menu-desktop-open", desktopOpen ? "1" : "0");
      this.setAttribute("data-menu-mobile-open", mobileOpen ? "1" : "0");

      if (this.layoutReady) {
        this.applyHostLayoutByState({ isMobile });
        this.applyContentLayout({ isMobile, desktopOpen, mobileOpen });
      }
    }

    renderError(message) {
      if (!this.shadowRoot) {
        return;
      }
      this.shadowRoot.replaceChildren();
      const errorNode = document.createElement("div");
      errorNode.textContent = message;
      this.shadowRoot.appendChild(errorNode);
      this.removeAttribute("loading");
      this.layoutReady = false;
      this.restoreContentStyles();
      this.restoreHostStyles();
    }

    applyHostLayoutStyles() {
      if (!this.hostStylesSnapshot) {
        this.hostStylesSnapshot = {
          display: this.style.display,
          position: this.style.position,
          top: this.style.top,
          left: this.style.left,
          right: this.style.right,
          width: this.style.width,
          height: this.style.height,
          zIndex: this.style.zIndex,
          pointerEvents: this.style.pointerEvents,
        };
      }

      this.style.display = "block";
      this.style.position = "fixed";
      this.style.top = "0";
      this.style.left = "0";
      this.style.right = "auto";
      this.style.width = `${DRAWER_COLLAPSED_WIDTH}px`;
      this.style.height = "100vh";
      this.style.zIndex = String(MENU_HOST_Z_INDEX);
      this.style.pointerEvents = "auto";
    }

    applyHostLayoutByState(state = {}) {
      const isMobile = Boolean(state.isMobile);
      if (isMobile) {
        this.style.width = "100%";
        this.style.height = "100vh";
        this.style.right = "0";
        this.style.pointerEvents = "none";
        return;
      }
      this.style.right = "auto";
      this.style.width = `${DRAWER_COLLAPSED_WIDTH}px`;
      this.style.height = "100vh";
      this.style.pointerEvents = "auto";
    }

    restoreHostStyles() {
      if (!this.hostStylesSnapshot) {
        return;
      }

      this.style.display = this.hostStylesSnapshot.display;
      this.style.position = this.hostStylesSnapshot.position;
      this.style.top = this.hostStylesSnapshot.top;
      this.style.left = this.hostStylesSnapshot.left;
      this.style.right = this.hostStylesSnapshot.right;
      this.style.width = this.hostStylesSnapshot.width;
      this.style.height = this.hostStylesSnapshot.height;
      this.style.zIndex = this.hostStylesSnapshot.zIndex;
      this.style.pointerEvents = this.hostStylesSnapshot.pointerEvents;
      this.hostStylesSnapshot = null;
    }

    resolveContentElement() {
      const selector = (this.getAttribute("app-content") || this.contentSelector || "#app").trim();
      if (!selector || selector.toLowerCase() === "none") {
        this.contentElement = null;
        return null;
      }

      this.contentSelector = selector;

      try {
        this.contentElement = document.querySelector(selector);
      } catch (error) {
        console.warn("app-menu: invalid app-content selector", selector, error);
        this.contentElement = null;
      }

      return this.contentElement;
    }

    applyContentLayout(state = {}) {
      const content = this.resolveContentElement();
      if (!content) {
        return;
      }

      if (!this.contentStylesSnapshot) {
        this.contentStylesSnapshot = {
          minHeight: content.style.minHeight,
          paddingLeft: content.style.paddingLeft,
          paddingTop: content.style.paddingTop,
          paddingBottom: content.style.paddingBottom,
          overflowX: content.style.overflowX,
          transition: content.style.transition,
        };
      }

      const isMobile = Boolean(state.isMobile);
      const desktopOpen = Boolean(state.desktopOpen);
      const nextPaddingLeft = !isMobile && desktopOpen
        ? `${DRAWER_OPEN_WIDTH}px`
        : `${DRAWER_COLLAPSED_WIDTH}px`;
      const nextPaddingTop = isMobile ? `${MOBILE_TOP_BAR_HEIGHT}px` : this.contentStylesSnapshot.paddingTop;
      const nextPaddingBottom = isMobile ? `${MOBILE_BOTTOM_BAR_HEIGHT}px` : this.contentStylesSnapshot.paddingBottom;
      const nextMinHeight = isMobile
        ? `calc(100dvh - ${MOBILE_VERTICAL_OFFSETS}px)`
        : "100vh";

      content.style.minHeight = nextMinHeight;
      content.style.paddingLeft = isMobile ? "0px" : nextPaddingLeft;
      content.style.paddingTop = nextPaddingTop;
      content.style.paddingBottom = nextPaddingBottom;
      content.style.overflowX = isMobile ? "hidden" : this.contentStylesSnapshot.overflowX;
      content.style.transition = "padding-left 180ms ease, padding-top 180ms ease, padding-bottom 180ms ease";
    }

    restoreContentStyles() {
      if (!this.contentStylesSnapshot) {
        return;
      }

      const content = this.resolveContentElement();
      if (!content) {
        this.contentStylesSnapshot = null;
        return;
      }

      content.style.minHeight = this.contentStylesSnapshot.minHeight;
      content.style.paddingLeft = this.contentStylesSnapshot.paddingLeft;
      content.style.paddingTop = this.contentStylesSnapshot.paddingTop;
      content.style.paddingBottom = this.contentStylesSnapshot.paddingBottom;
      content.style.overflowX = this.contentStylesSnapshot.overflowX;
      content.style.transition = this.contentStylesSnapshot.transition;
      this.contentStylesSnapshot = null;
    }
  }

  customElements.define("app-menu", AppMenu);
}

async function fetchMenuPayload(src, logoutUrl, options = {}) {
  const { forceRefresh = false } = options;
  const cacheBust = forceRefresh ? String(Date.now()) : "";
  const jsonUrl = withMenuQuery(src, {
    format: "json",
    shape: "full",
    _mt: cacheBust,
  });

  const fallbackUrl = withMenuQuery(src, {
    format: "json",
    _mt: cacheBust,
  });

  try {
    return await fetchJson(jsonUrl, logoutUrl);
  } catch (error) {
    if (fallbackUrl === jsonUrl) {
      throw error;
    }
    return await fetchJson(fallbackUrl, logoutUrl);
  }
}

async function fetchJson(url, logoutUrl) {
  const response = await fetch(url, {
    credentials: "same-origin",
    cache: "no-store",
  });

  if (response.status === 401) {
    window.location.href = logoutUrl;
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return payload;
}

async function getExtensions(source) {
  if (!source) {
    return [];
  }

  if (looksLikeJson(source)) {
    try {
      return normalizeExtensions(JSON.parse(source));
    } catch (error) {
      console.warn("app-menu: invalid inline extensions json", error);
      return [];
    }
  }

  try {
    const response = await fetch(source, {
      credentials: "same-origin",
    });

    if (!response.ok) {
      console.warn("app-menu: unable to fetch extensions", response.status);
      return [];
    }

    const data = await response.json();
    return normalizeExtensions(data);
  } catch (error) {
    console.warn("app-menu: unable to parse extensions", error);
    return [];
  }
}

function withMenuQuery(source, nextQuery) {
  try {
    const base = new URL(source, window.location.origin);
    Object.entries(nextQuery).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      base.searchParams.set(key, value);
    });
    return base.toString();
  } catch (_error) {
    return source;
  }
}
